import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { templateService } from '../services/templateService'
import { TemplateCategory } from '../types/wordTemplate'
import './Templates.css'

function formatCategoryName(category: string): string {
  return category.charAt(0).toUpperCase() + category.slice(1)
}

export function Templates() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory | 'all'>('all')

  const { data: templates, isLoading, error } = useQuery({
    queryKey: ['templates', selectedCategory],
    queryFn: () => templateService.getAll(selectedCategory === 'all' ? undefined : selectedCategory),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => templateService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to archive this template?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const categoryOptions: { value: TemplateCategory | 'all'; label: string }[] = [
    { value: 'all', label: 'All Categories' },
    { value: 'letter', label: 'Letters' },
    { value: 'form', label: 'Forms' },
    { value: 'referral', label: 'Referrals' },
    { value: 'certificate', label: 'Certificates' },
    { value: 'prescription', label: 'Prescriptions' },
    { value: 'report', label: 'Reports' },
    { value: 'other', label: 'Other' },
  ]

  if (isLoading) {
    return <div className="templates-page">Loading templates...</div>
  }

  if (error) {
    return <div className="templates-page">Error loading templates: {String(error)}</div>
  }

  return (
    <div className="templates-page">
      <div className="templates-header">
        <h1>Word Templates</h1>
        <div className="templates-header-actions">
          <button className="btn-secondary" onClick={() => navigate('/templates/help')}>
            Template Variables
          </button>
          <button className="btn-primary" onClick={() => navigate('/templates/new')}>
            + New Template
          </button>
        </div>
      </div>

      <div className="templates-filters">
        <label>
          Category:
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value as TemplateCategory | 'all')}
          >
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="templates-grid">
        {templates && templates.length > 0 ? (
          templates.map((template) => (
            <div key={template.id} className="template-card">
              <div className="template-card-header">
                <h3>{template.name}</h3>
                <span className={`template-category category-${template.category}`}>
                  {formatCategoryName(template.category)}
                </span>
              </div>
              {template.description && (
                <p className="template-description">{template.description}</p>
              )}
              <div className="template-meta">
                <span className="template-variables">
                  {template.variables?.length || 0} variables
                </span>
                <span className="template-date">
                  Updated: {new Date(template.updatedAt).toLocaleDateString()}
                </span>
              </div>
              <div className="template-actions">
                <Link to={`/templates/${template.id}`} className="btn-secondary">
                  View
                </Link>
                <Link to={`/templates/${template.id}/edit`} className="btn-secondary">
                  Edit
                </Link>
                <button
                  className="btn-danger"
                  onClick={() => handleDelete(template.id)}
                  disabled={deleteMutation.isPending}
                >
                  Archive
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="templates-empty">
            <p>No templates found. Create your first template to get started.</p>
            <button className="btn-primary" onClick={() => navigate('/templates/new')}>
              Create Template
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
































